// src/services/tallyExportService.js
// Generates Tally ERP 9 compatible XML for import
// Supports: Sales Vouchers (Invoices); Payments & Receipts; Purchase Vouchers.
// D-291 P3d-5c-1: purchases come from the vendor's BILL (purchase_bills) via
//   src/utils/tallyBill.js; the old one-voucher-per-PO export only for POs
//   received before bills existed, never for a PO a bill / new-flow receipt
//   points at.
// D-291 P3d-5c-2: money movement (vendor / subcontractor payments with TDS,
//   expense / transport payments, payment reversals, subcontractor bills,
//   money received) via src/utils/tallyMoney.js - the same rule as the
//   Cash / Bank Book and the khaata. The old export that sent EVERY expense /
//   transport ledger row as cash paid (incl. project cost allocations and
//   subcontractor bills) is gone.

import { planTallyPurchase, tallyLedgersOf, isLegacyPoForTally } from "../utils/tallyBill";
import { planTallyMoney, moneyLedgersOf } from "../utils/tallyMoney";

// ─── GST determination ───────────────────────────────────
// If company state_code === client state_code → CGST + SGST (intra-state)
// Else → IGST (inter-state)
// If state unknown → default to CGST + SGST

const isInterState = (companyStateCode, clientStateCode) => {
  if (!companyStateCode || !clientStateCode) return false; // default intra
  return companyStateCode !== clientStateCode;
};

// ─── Date format: YYYYMMDD ────────────────────────────────
const tallyDate = (dateInput) => {
  if (!dateInput) return "";
  let d;
  if (dateInput?.seconds) d = new Date(dateInput.seconds * 1000);
  else if (dateInput instanceof Date) d = dateInput;
  else d = new Date(dateInput);
  if (isNaN(d)) return "";
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${dy}`;
};

// ─── Escape XML special chars ─────────────────────────────
const esc = (str) =>
  String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// ─── Tally XML envelope wrapper ───────────────────────────
const wrapEnvelope = (vouchersXml) => `<?xml version="1.0" encoding="UTF-8"?>
<ENVELOPE>
  <HEADER>
    <TALLYREQUEST>Import Data</TALLYREQUEST>
  </HEADER>
  <BODY>
    <IMPORTDATA>
      <REQUESTDESC>
        <REPORTNAME>Vouchers</REPORTNAME>
        <STATICVARIABLES>
          <SVCURRENTCOMPANY>##SVCURRENTCOMPANY</SVCURRENTCOMPANY>
        </STATICVARIABLES>
      </REQUESTDESC>
      <REQUESTDATA>
        ${vouchersXml}
      </REQUESTDATA>
    </IMPORTDATA>
  </BODY>
</ENVELOPE>`;

// ═══════════════════════════════════════════════════════════
// SALES VOUCHER — from Invoice
// ═══════════════════════════════════════════════════════════
const buildSalesVoucher = (invoice, company, config) => {
  const date         = tallyDate(invoice.invoice_date || invoice.created_at);
  const clientName   = esc(invoice.client_name || "Unknown Party");
  const invoiceNo    = esc(invoice.invoice_no  || "");
  const narration    = esc(`Invoice ${invoiceNo} - ${clientName}`);
  const totalAmount  = Number(invoice.total_amount  || 0);
  const subtotal     = Number(invoice.subtotal       || totalAmount);
  const discount     = Number(invoice.discount       || 0);
  const taxableAmt   = subtotal - discount;
  const taxAmount    = Number(invoice.tax_amount     || 0);

  // GST split
  const interState   = isInterState(company.state_code, invoice.client_state_code);
  const salesLedger  = esc(config.tally_sales_ledger  || "Service Revenue");
  const cgstLedger   = esc(config.tally_cgst_ledger   || "Output CGST");
  const sgstLedger   = esc(config.tally_sgst_ledger   || "Output SGST");
  const igstLedger   = esc(config.tally_igst_ledger   || "Output IGST");

  // GST entries
  let gstEntries = "";
  if (taxAmount > 0) {
    if (interState) {
      gstEntries = `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${igstLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${taxAmount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`;
    } else {
      const half = (taxAmount / 2).toFixed(2);
      gstEntries = `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${cgstLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${half}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${sgstLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${half}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`;
    }
  }

  return `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER REMOTEID="${esc(invoice.id)}" VCHTYPE="Sales" ACTION="Create" OBJVIEW="Invoice Voucher View">
        <DATE>${date}</DATE>
        <REFERNCENO>${invoiceNo}</REFERNCENO>
        <VOUCHERTYPENAME>Sales</VOUCHERTYPENAME>
        <PARTYLEDGERNAME>${clientName}</PARTYLEDGERNAME>
        <NARRATION>${narration}</NARRATION>
        <PERSISTEDVIEW>Invoice Voucher View</PERSISTEDVIEW>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${clientName}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${totalAmount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${salesLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${taxableAmt.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        ${gstEntries}
      </VOUCHER>
    </TALLYMESSAGE>`;
};

// ═══════════════════════════════════════════════════════════
// PURCHASE VOUCHER — from PO (ONLY old POs, received before bills existed)
// ═══════════════════════════════════════════════════════════
const buildPurchaseVoucher = (po, company, config) => {
  const date           = tallyDate(po.created_at || po.grn_completed_at);
  const vendorName     = esc(po.vendor_name || "Unknown Vendor");
  const poNo           = esc(po.po_number   || po.reference_no || "");
  const narration      = esc(`PO ${poNo} - ${vendorName}`);
  const totalAmount    = Number(po.grand_total || po.total_amount || po.amount || 0);
  const purchaseLedger = esc(config.tally_purchase_ledger || "Purchases");

  // Input GST — assume 18% if tax info present
  const taxPercent   = Number(po.tax_percent || 0);
  const taxAmount    = taxPercent > 0
    ? (totalAmount - totalAmount / (1 + taxPercent / 100))
    : 0;
  const taxableAmt   = totalAmount - taxAmount;

  const interState   = isInterState(company.state_code, po.vendor_state_code);
  const inputCGST    = esc(config.tally_input_cgst || "Input CGST");
  const inputSGST    = esc(config.tally_input_sgst || "Input SGST");
  const inputIGST    = esc(config.tally_input_igst || "Input IGST");

  let gstEntries = "";
  if (taxAmount > 0) {
    if (interState) {
      gstEntries = `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${inputIGST}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${taxAmount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`;
    } else {
      const half = (taxAmount / 2).toFixed(2);
      gstEntries = `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${inputCGST}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${half}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${inputSGST}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${half}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`;
    }
  }

  return `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER REMOTEID="${esc(po.id)}" VCHTYPE="Purchase" ACTION="Create" OBJVIEW="Accounting Voucher View">
        <DATE>${date}</DATE>
        <REFERNCENO>${poNo}</REFERNCENO>
        <VOUCHERTYPENAME>Purchase</VOUCHERTYPENAME>
        <PARTYLEDGERNAME>${vendorName}</PARTYLEDGERNAME>
        <NARRATION>${narration}</NARRATION>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${vendorName}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${totalAmount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${purchaseLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${taxableAmt.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        ${gstEntries}
      </VOUCHER>
    </TALLYMESSAGE>`;
};

// ─── One ledger line (lines from tallyBill / tallyMoney: drP > 0 debit, < 0 credit, paise) ───
const billAmount = (p) => (Math.abs(p) / 100).toFixed(2);
const ledgerLinesXml = (lines) => lines.map((l) => `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${esc(l.ledger)}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>${l.drP > 0 ? "Yes" : "No"}</ISDEEMEDPOSITIVE>
          <AMOUNT>${l.drP > 0 ? "-" : ""}${billAmount(l.drP)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`).join("");

// ═══════════════════════════════════════════════════════════
// PURCHASE VOUCHER — from a vendor BILL (D-291 P3d-5c-1)
// ═══════════════════════════════════════════════════════════
const buildBillPurchaseVoucher = (plan, bill) => {
  const date      = plan.date ? plan.date.replace(/-/g, "") : tallyDate(bill.created_at);
  const vendor    = esc(plan.vendor);
  const ref       = esc(plan.ref);
  const kind      = bill.bill_type === "proforma" ? "Bill (no GSTIN)" : "Tax invoice";
  const narration = esc(`${kind} ${plan.ref} - PO ${plan.po_number} - ${plan.vendor}`);

  return `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER REMOTEID="${esc(`pbill_${plan.bill_id}`)}" VCHTYPE="Purchase" ACTION="Create" OBJVIEW="Accounting Voucher View">
        <DATE>${date}</DATE>
        <REFERENCE>${ref}</REFERENCE>
        <REFERENCEDATE>${date}</REFERENCEDATE>
        <VOUCHERTYPENAME>Purchase</VOUCHERTYPENAME>
        <PARTYLEDGERNAME>${vendor}</PARTYLEDGERNAME>
        <NARRATION>${narration}</NARRATION>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${vendor}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${billAmount(plan.totalP)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>${ledgerLinesXml(plan.entries)}
      </VOUCHER>
    </TALLYMESSAGE>`;
};

/**
 * What the purchase export contains (D-291 P3d-5c-1).
 * bills = purchase_bills in the date range; allBills = every purchase_bill of
 * the company (to recognise billed POs); pos = POs in the range; receipts = purchase_receipts.
 */
export const planPurchaseExport = ({ bills = [], allBills = [], pos = [], receipts = [], company = {} } = {}) => {
  const ledgers = tallyLedgersOf(company);
  const plans = bills.map((bill) => ({ bill, plan: planTallyPurchase(bill, ledgers) }));
  const receiptsById = Object.fromEntries(receipts.map((r) => [r.id, r]));
  // Second guard: a PO that ANY bill or new-flow receipt points at is never an old-style PO voucher.
  const billFlowPoIds = new Set([
    ...allBills.map((b) => b.purchase_order_id).filter(Boolean),
    ...receipts.filter((r) => String(r.bill_status || "").trim() !== "").map((r) => r.purchase_order_id).filter(Boolean),
  ]);
  return {
    exportBills:  plans.filter((x) => x.plan.status === "export"),
    heldBills:    plans.filter((x) => x.plan.status === "held"),
    problemBills: plans.filter((x) => x.plan.status === "problem"),
    legacyPos:    pos.filter((po) => isLegacyPoForTally(po, receiptsById) && !billFlowPoIds.has(po.id)
      && Number(po.grand_total || po.total_amount || po.amount || 0) > 0),
  };
};

const purchaseVouchers = (purchase, company, config) => [
  ...(purchase?.exportBills || []).map(({ bill, plan }) => buildBillPurchaseVoucher(plan, bill)),
  ...(purchase?.legacyPos || []).map((po) => buildPurchaseVoucher(po, company, config)),
];

// ═══════════════════════════════════════════════════════════
// PAYMENTS & RECEIPTS — money movement (D-291 P3d-5c-2)
// ═══════════════════════════════════════════════════════════
const MONEY_VCH = { payment: "Payment", receipt: "Receipt", journal: "Journal" };

const buildMoneyVoucher = (v) => {
  const type = MONEY_VCH[v.kind] || "Journal";
  const date = v.at ? tallyDate(new Date(v.at)) : "";
  // The party is named only when it IS one of the voucher's ledgers (an expense
  // payee such as a driver is not a Tally ledger).
  const partyIsLedger = v.party && v.lines.some((l) => l.ledger === v.party);
  return `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER REMOTEID="${esc(v.id)}" VCHTYPE="${type}" ACTION="Create" OBJVIEW="Accounting Voucher View">
        <DATE>${date}</DATE>${v.ref ? `
        <REFERENCE>${esc(v.ref)}</REFERENCE>` : ""}
        <VOUCHERTYPENAME>${type}</VOUCHERTYPENAME>${partyIsLedger ? `
        <PARTYLEDGERNAME>${esc(v.party)}</PARTYLEDGERNAME>` : ""}
        <NARRATION>${esc(v.narration)}</NARRATION>${ledgerLinesXml(v.lines)}
      </VOUCHER>
    </TALLYMESSAGE>`;
};

/**
 * What the money export contains: rows = every financial_transactions row of the
 * company; logs = subcontractor_work_logs (names for old subcontractor bills).
 * Returns planTallyMoney()'s { vouchers, problems, skipped } (vouchers not date-filtered).
 */
export const planMoneyExport = ({ rows = [], logs = [], clients = [], company = {} } = {}) =>
  planTallyMoney(rows, {
    ledgers: moneyLedgersOf(company),
    logsById: Object.fromEntries(logs.map((l) => [l.id, l])),
    clientsById: Object.fromEntries(clients.map((c) => [c.id, c])),
  });

// ═══════════════════════════════════════════════════════════
// MAIN EXPORT FUNCTIONS
// ═══════════════════════════════════════════════════════════

export const generateSalesXML = (invoices, company, config) => {
  const vouchers = invoices
    .filter(inv => Number(inv.total_amount || 0) > 0)
    .map(inv => buildSalesVoucher(inv, company, config))
    .join("\n");
  return wrapEnvelope(vouchers);
};

/** moneyVouchers = planMoneyExport().vouchers (date-filtered by the page). */
export const generateMoneyXML = (moneyVouchers = []) =>
  wrapEnvelope(moneyVouchers.map(buildMoneyVoucher).join("\n"));

/** purchase = the result of planPurchaseExport(). */
export const generatePurchaseXML = (purchase, company, config) =>
  wrapEnvelope(purchaseVouchers(purchase, company, config).join("\n"));

export const generateAllXML = (invoices, moneyVouchers, purchase, company, config) => {
  const salesV    = invoices
    .filter(inv => Number(inv.total_amount || 0) > 0)
    .map(inv => buildSalesVoucher(inv, company, config));
  const moneyV    = (moneyVouchers || []).map(buildMoneyVoucher);
  const purchaseV = purchaseVouchers(purchase, company, config);
  return wrapEnvelope([...salesV, ...purchaseV, ...moneyV].join("\n"));
};

// ─── Download helper ─────────────────────────────────────
export const downloadXML = (xmlContent, filename) => {
  const blob = new Blob([xmlContent], { type: "text/xml;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href  = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};